import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Users, CreditCard, Receipt, LifeBuoy, Bot, FileSearch, ScrollText,
  Store, FolderLock, Gauge, FileText, ClipboardList, Activity, Settings, Menu, X, LogOut, ShieldCheck, ExternalLink, Mail,
} from "lucide-react";
import { useAuth } from "../AuthContext";

const GROUPS = [
  { title: "Overview", items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  { title: "Customers", items: [
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
    { to: "/admin/billing", label: "Billing", icon: Receipt },
    { to: "/admin/support", label: "Support", icon: LifeBuoy },
  ]},
  { title: "Platform", items: [
    { to: "/admin/ai-operations", label: "AI Operations", icon: Bot },
    { to: "/admin/reports", label: "Credit Reports", icon: FileSearch },
    { to: "/admin/disputes", label: "Disputes", icon: ScrollText },
    { to: "/admin/vendors", label: "Vendors", icon: Store },
    { to: "/admin/documents", label: "Documents", icon: FolderLock },
    { to: "/admin/funding", label: "Funding", icon: Gauge },
    { to: "/admin/content", label: "Content", icon: FileText },
  ]},
  { title: "Operations", items: [
    { to: "/admin/emails", label: "Email Delivery", icon: Mail },
    { to: "/admin/audit-logs", label: "Audit Logs", icon: ClipboardList },
    { to: "/admin/system", label: "System Health", icon: Activity },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ]},
];

const Brand = () => (
  <Link to="/admin" className="flex items-center gap-2.5 px-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-xl cc-gradient-br cc-glow-blue"><ShieldCheck size={18} className="text-white" /></div>
    <div className="leading-tight">
      <div className="cc-heading text-[15px] font-600 text-white">ArcaCentum</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-blue-300/80">Admin</div>
    </div>
  </Link>
);

const NavItems = ({ onNav }) => (
  <nav className="flex-1 overflow-y-auto cc-scroll px-3 py-4 space-y-4">
    {GROUPS.map((g) => (
      <div key={g.title}>
        <div className="px-3 pb-1.5 text-[10px] tracking-[0.2em] uppercase text-slate-600">{g.title}</div>
        <div className="space-y-0.5">
          {g.items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.end} onClick={onNav}
              data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `cc-nav-item flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${isActive ? "cc-nav-active" : "text-slate-400"}`}>
              <it.icon size={16} strokeWidth={1.8} />{it.label}
            </NavLink>
          ))}
        </div>
      </div>
    ))}
  </nav>
);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const initials = (user?.name || "A").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  const crumbs = loc.pathname.replace(/^\//, "").split("/").filter(Boolean);
  const doLogout = () => { logout(); navigate("/credit/login"); };

  return (
    <div className="cc cc-bg min-h-screen text-slate-200">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col cc-glass border-r border-white/[0.06] z-30">
        <div className="h-16 flex items-center border-b border-white/[0.06] px-4"><Brand /></div>
        <NavItems />
        <div className="border-t border-white/[0.06] p-3">
          <Link to="/credit/app" className="cc-nav-item flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-slate-500"><ExternalLink size={14} /> Back to user app</Link>
        </div>
      </aside>

      <AnimatePresence>
        {open && (
          <>
            <motion.div className="lg:hidden fixed inset-0 bg-black/60 z-40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.aside className="lg:hidden fixed inset-y-0 left-0 w-72 flex flex-col cc-surface border-r border-white/[0.06] z-50"
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.3 }}>
              <div className="h-16 flex items-center justify-between border-b border-white/[0.06] px-4"><Brand /><button onClick={() => setOpen(false)} className="text-slate-400 p-2"><X size={20} /></button></div>
              <NavItems onNav={() => setOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between gap-4 px-4 sm:px-6 cc-glass border-b border-white/[0.06]">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden text-slate-300 p-2 -ml-2" onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu size={22} /></button>
            <div className="flex items-center gap-1.5 text-sm text-slate-500 truncate">
              {crumbs.map((c, i) => <span key={i} className={i === crumbs.length - 1 ? "text-slate-300 capitalize" : "capitalize"}>{c}{i < crumbs.length - 1 && <span className="mx-1.5 text-slate-700">/</span>}</span>)}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm text-white leading-tight">{user?.name}</div>
              <div className="text-[11px] text-blue-300 capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
            <span className="flex h-9 w-9 items-center justify-center rounded-full cc-gradient text-white text-xs font-600">{initials}</span>
            <button onClick={doLogout} className="text-slate-400 hover:text-red-300 p-2" title="Log out" data-testid="admin-logout"><LogOut size={18} /></button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
